import { fromArray } from "client_legacy/ndaw/mapHelpers";
import { ensureNotNil, isNotNil } from "../Misc/utils";
import { TimeZoneOption, timeZoneOptions } from "./timeZoneOptions";

export type TimeZone = {
	identifier: string;
	name: string;
	mainCity: string;
	offset: string;
	offsetInMinutes: number;
	isSupportedByWorkspace: boolean;
};

let cacheLocale: string | null = null;
let allTimeZonesCache: (Map<string, TimeZone>) | null = null;

export function getSystemTimeZone(locale: string): TimeZone {
	const identifier: string = Intl.DateTimeFormat()
		.resolvedOptions()
		.timeZone;

	return getTimeZone(identifier, locale);
}

export function getUtcTimeZone(locale: string): TimeZone {
	return getTimeZone("Etc/UTC", locale);
}

export function listTimeZones(locale: string): readonly TimeZone[] {
	ensureCacheLocale(locale);

	if (isNotNil(allTimeZonesCache)) {
		return Array.from(allTimeZonesCache.values());
	}

	const result: TimeZone[] = timeZoneOptions.map(
		x => createTimeZone(
			x.identifier,
			locale,
			x
		)
	);

	allTimeZonesCache = fromArray(
		result,
		tz => tz.identifier
	);

	cacheLocale = locale;

	return result;
}

export function resolveTimeZoneWithFallbackToEtcEra(
	identifier: string,
	locale: string,
	offsetInMinutes: number
): TimeZone {
	let timeZoneRetrieval: TryResult<TimeZone> = tryGetTimeZone(identifier, locale);

	if (timeZoneRetrieval.wasSuccessful) {
		return timeZoneRetrieval.result;
	}

	timeZoneRetrieval = tryCreateApproximateEtcEraTimeZone(offsetInMinutes, locale);

	if (timeZoneRetrieval.wasSuccessful) {
		return timeZoneRetrieval.result;
	}

	console.error(`Unable to resolve the timezone with the identifier: ${identifier} and offset ${offsetInMinutes}. Falling back to system timezone.`);
	return getSystemTimeZone(locale);
}

export function tryGetTimeZone(identifier: string, locale: string): TryResult<TimeZone> {
	try {
		return {
			wasSuccessful: true,
			result: getTimeZone(identifier, locale)
		};
	} catch(e) {
		if (e instanceof RangeError) {
			return {
				wasSuccessful: false
			};
		}

		throw e;
	}
}

export function areEqual(timeZone1: TimeZone | nil, timeZone2: TimeZone | nil): boolean {
	if (!timeZone1 && !timeZone2) {
		return true;
	}

	if (!timeZone1 || !timeZone2) {
		return false;
	}

	return timeZone1.identifier === timeZone2.identifier;
}

function getTimeZone(identifier: string, locale: string): TimeZone {
	ensureCacheLocale(locale);

	if (isNotNil(allTimeZonesCache)) {
		const valueFromCache: TimeZone | undefined = allTimeZonesCache.get(identifier);

		if (isNotNil(valueFromCache)) {
			return valueFromCache;
		}
	}

	return createTimeZone(
		identifier,
		locale,
		undefined
	);
}

function createTimeZone(
	identifier: string,
	locale: string,
	timeZoneOption: TimeZoneOption | undefined
): TimeZone {
	const timeZoneName: string = getTimeZoneName(locale, identifier, "longGeneric");
	const timeZoneOffset: string = getTimeZoneName(locale, identifier, "shortOffset");
	const timeZoneLongOffset: string = getTimeZoneName("en-US", identifier, "longOffset");
	let offsetInMinutes: number;

	if (timeZoneLongOffset === "GMT") {
		offsetInMinutes = 0;
	} else {
		const offsetMatch: RegExpExecArray | null = /^GMT([+-])(\d{2}):(\d{2})$/.exec(timeZoneLongOffset);
		if (offsetMatch) {
			const sign: string = offsetMatch[1];
			const hours: string = offsetMatch[2];
			const minutes: string = offsetMatch[3];

			offsetInMinutes = Number(minutes) + Number(hours) * 60;
			if (sign === "-") {
				offsetInMinutes *= -1;
			}
		} else {
			throw new Error(`Unknown offset format ${timeZoneLongOffset}`);
		}
	}

	if (!timeZoneOption) {
		timeZoneOption = timeZoneOptions.find(x => x.identifier === identifier);
	}

	return {
		identifier,
		name: timeZoneName,
		offset: timeZoneOffset,
		offsetInMinutes: offsetInMinutes,
		mainCity: isNotNil(timeZoneOption)
			? timeZoneOption.cityName
			: extractCityNameFromIdentifier(identifier),
		isSupportedByWorkspace: isNotNil(timeZoneOption)
	};
}

function extractCityNameFromIdentifier(identifier: string): string {
	const parts: string[] = identifier.split("/");
	return parts[parts.length - 1];
}

function ensureCacheLocale(locale: string): void {
	if (cacheLocale === locale) {
		return;
	}

	allTimeZonesCache = null;
	cacheLocale = null;
}

type TimeZoneName =
	| "short"
	| "long"
	| "shortOffset"
	| "longOffset"
	| "shortGeneric"
	| "longGeneric";

function getTimeZoneName(locale: string, timeZone: string, timeZoneName: TimeZoneName): string {
	const format = new Intl.DateTimeFormat(
		locale,
		{
			timeZone,
			timeZoneName
		}
	);

	const parts: Intl.DateTimeFormatPart[] = format.formatToParts();

	return ensureNotNil(
		parts.find(p => p.type === "timeZoneName")
	)
		.value;
}

function tryCreateApproximateEtcEraTimeZone(
	offsetInMinutes: number,
	locale: string
): TryResult<TimeZone> {
	const offsetInHours: number = Math.round(offsetInMinutes / 60);

	if (offsetInHours < -12 || offsetInHours > 14) {
		return { wasSuccessful: false };
	}

	// NOTE: Etc era timezone format uses reversed offset
	const reversedOffset: number = -offsetInHours;

	const identifier = reversedOffset === 0
		? "Etc/GMT"
		: reversedOffset < 0
			? `Etc/GMT${reversedOffset}`
			: `Etc/GMT+${reversedOffset}`;

	return {
		wasSuccessful: true,
		result: createTimeZone(
			identifier,
			locale,
			undefined
		)
	};
}